import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplications } from '../context/ApplicationContext';
import { useWorkflow } from '../context/WorkflowContext';
import { useAuth } from '../context/AuthContext';
import UserLayout from '../components/UserLayout';
import Card, { CardHeader, CardBody } from '../components/Card';
import Button from '../components/Button';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, SearchIcon, FilterIcon, CalendarIcon, BuildingIcon, CreditCardIcon, UserIcon, ChevronRightIcon, ArrowRightIcon, TagIcon, BarChart4Icon } from 'lucide-react';
const MyApplicationsPage: React.FC = () => {
  const {
    user
  } = useAuth();
  const {
    getApplicationsByUserId
  } = useApplications();
  const {
    getWorkflowInstanceByApplicationId
  } = useWorkflow();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date-desc');
  const [applicationWorkflows, setApplicationWorkflows] = useState<Record<string, any>>({});
  useEffect(() => {
    if (user) {
      const userApplications = getApplicationsByUserId(user.id);
      setApplications(userApplications);
      // Get workflow instances for each application
      const workflows: Record<string, any> = {};
      userApplications.forEach(app => {
        const workflow = getWorkflowInstanceByApplicationId(app.id);
        if (workflow) {
          workflows[app.id] = workflow;
        }
      });
      setApplicationWorkflows(workflows);
      // Apply initial filters
      applyFilters(userApplications, searchQuery, statusFilter, sortBy);
    }
  }, [user, getApplicationsByUserId, getWorkflowInstanceByApplicationId]);
  const applyFilters = (apps: any[], query: string, status: string, sort: string) => {
    // Filter by search query
    let filtered = apps;
    if (query) {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(app => app.serviceName.toLowerCase().includes(lowercaseQuery) || app.referenceNumber.toLowerCase().includes(lowercaseQuery) || app.mdaName.toLowerCase().includes(lowercaseQuery));
    }
    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(app => app.status === status);
    }
    // Sort applications
    switch (sort) {
      case 'date-desc':
        filtered.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
        break;
      case 'date-asc':
        filtered.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.serviceName.localeCompare(b.serviceName));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.serviceName.localeCompare(a.serviceName));
        break;
      default:
        break;
    }
    setFilteredApplications(filtered);
  };
  useEffect(() => {
    applyFilters(applications, searchQuery, statusFilter, sortBy);
  }, [searchQuery, statusFilter, sortBy, applications]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applyFilters(applications, searchQuery, statusFilter, sortBy);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5557">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-5558" />
            Approved
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5559">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-5560" />
            Rejected
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-5561">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-5562" />
            Pending
          </span>;
      case 'processing':
      case 'under_review':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-5563">
            <BarChart4Icon className="w-3 h-3 mr-1" data-id="element-5564" />
            Processing
          </span>;
      case 'awaiting_payment':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-5565">
            <CreditCardIcon className="w-3 h-3 mr-1" data-id="element-5566" />
            Awaiting Payment
          </span>;
      case 'awaiting_documents':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-5567">
            <FileTextIcon className="w-3 h-3 mr-1" data-id="element-5568" />
            Awaiting Documents
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5569">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-5570" />
            {status.replace('_', ' ').toUpperCase()}
          </span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getApplicationStats = () => {
    return {
      total: applications.length,
      pending: applications.filter(app => app.status === 'pending').length,
      approved: applications.filter(app => app.status === 'approved').length,
      rejected: applications.filter(app => app.status === 'rejected').length,
      processing: applications.filter(app => app.status === 'processing' || app.status === 'under_review').length,
      awaiting_payment: applications.filter(app => app.status === 'awaiting_payment').length
    };
  };
  const stats = getApplicationStats();
  return <UserLayout data-id="element-5571">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-5572">
        <div className="max-w-7xl mx-auto" data-id="element-5573">
          <div className="mb-6" data-id="element-5574">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5575">
              My Applications
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-5576">
              View and manage all your service applications in one place.
            </p>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-id="element-5577">
            <Card data-id="element-5578">
              <CardBody className="p-4" data-id="element-5579">
                <div className="flex flex-col items-center" data-id="element-5580">
                  <div className="text-gray-500 text-sm mb-1" data-id="element-5581">Total</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-5582">
                    {stats.total}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-5583">
              <CardBody className="p-4 bg-yellow-50" data-id="element-5584">
                <div className="flex flex-col items-center" data-id="element-5585">
                  <div className="text-yellow-700 text-sm mb-1" data-id="element-5586">Pending</div>
                  <div className="text-2xl font-bold text-yellow-700" data-id="element-5587">
                    {stats.pending}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-5588">
              <CardBody className="p-4 bg-blue-50" data-id="element-5589">
                <div className="flex flex-col items-center" data-id="element-5590">
                  <div className="text-blue-700 text-sm mb-1" data-id="element-5591">Processing</div>
                  <div className="text-2xl font-bold text-blue-700" data-id="element-5592">
                    {stats.processing}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-5593">
              <CardBody className="p-4 bg-purple-50" data-id="element-5594">
                <div className="flex flex-col items-center" data-id="element-5595">
                  <div className="text-purple-700 text-sm mb-1" data-id="element-5596">
                    Awaiting Payment
                  </div>
                  <div className="text-2xl font-bold text-purple-700" data-id="element-5597">
                    {stats.awaiting_payment}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-5598">
              <CardBody className="p-4 bg-green-50" data-id="element-5599">
                <div className="flex flex-col items-center" data-id="element-5600">
                  <div className="text-green-700 text-sm mb-1" data-id="element-5601">Approved</div>
                  <div className="text-2xl font-bold text-green-700" data-id="element-5602">
                    {stats.approved}
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card data-id="element-5603">
              <CardBody className="p-4 bg-red-50" data-id="element-5604">
                <div className="flex flex-col items-center" data-id="element-5605">
                  <div className="text-red-700 text-sm mb-1" data-id="element-5606">Rejected</div>
                  <div className="text-2xl font-bold text-red-700" data-id="element-5607">
                    {stats.rejected}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          {/* Filters */}
          <Card className="mb-6" data-id="element-5608">
            <CardBody className="p-4" data-id="element-5609">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-5610">
                <form onSubmit={handleSearch} className="relative flex-grow max-w-md" data-id="element-5611">
                  <input type="text" placeholder="Search applications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-5612" />
                  <SearchIcon size={20} className="absolute left-3 top-2.5 text-gray-400" data-id="element-5613" />
                  <button type="submit" className="absolute right-3 top-2.5 text-gray-400 hover:text-green-600" aria-label="Search" data-id="element-5614">
                    <span className="sr-only" data-id="element-5615">Search</span>
                  </button>
                </form>
                <div className="flex flex-wrap items-center space-x-2" data-id="element-5616">
                  <div className="flex items-center" data-id="element-5617">
                    <FilterIcon size={16} className="mr-2 text-gray-500" data-id="element-5618" />
                    <span className="text-sm text-gray-700 mr-2" data-id="element-5619">Status:</span>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-5620">
                      <option value="all" data-id="element-5621">All</option>
                      <option value="awaiting_payment" data-id="element-5622">Awaiting Payment</option>
                      <option value="pending" data-id="element-5623">Pending</option>
                      <option value="processing" data-id="element-5624">Processing</option>
                      <option value="under_review" data-id="element-5625">Under Review</option>
                      <option value="approved" data-id="element-5626">Approved</option>
                      <option value="rejected" data-id="element-5627">Rejected</option>
                    </select>
                  </div>
                  <div className="flex items-center" data-id="element-5628">
                    <span className="text-sm text-gray-700 mr-2" data-id="element-5629">Sort:</span>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-5630">
                      <option value="date-desc" data-id="element-5631">Newest First</option>
                      <option value="date-asc" data-id="element-5632">Oldest First</option>
                      <option value="name-asc" data-id="element-5633">Name (A-Z)</option>
                      <option value="name-desc" data-id="element-5634">Name (Z-A)</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
          {/* Applications List */}
          {filteredApplications.length === 0 ? <Card data-id="element-5635">
              <CardBody className="p-8 text-center" data-id="element-5636">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-5637">
                  <FileTextIcon className="h-6 w-6 text-green-600" data-id="element-5638" />
                </div>
                <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-5639">
                  No applications found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-5640">
                  {applications.length === 0 ? "You haven't submitted any applications yet." : 'No applications match your current filters.'}
                </p>
                {applications.length === 0 && <div className="mt-6" data-id="element-5641">
                    <Button variant="primary" onClick={() => navigate('/mdas')} data-id="element-5642">
                      Browse Services
                    </Button>
                  </div>}
              </CardBody>
            </Card> : <div className="space-y-4" data-id="element-5643">
              {filteredApplications.map(application => <Card key={application.id} className="hover:shadow-md transition-shadow" data-id="element-5644">
                  <CardBody className="p-0" data-id="element-5645">
                    <div className="p-4" data-id="element-5646">
                      <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-5647">
                        <div data-id="element-5648">
                          <h3 className="text-lg font-medium text-gray-900" data-id="element-5649">
                            {application.serviceName}
                          </h3>
                          <div className="flex flex-wrap items-center mt-1 space-x-2" data-id="element-5650">
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-5651">
                              <TagIcon className="h-3 w-3 mr-1" data-id="element-5652" />
                              {application.referenceNumber}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-5653">
                              <CalendarIcon className="h-3 w-3 mr-1" data-id="element-5654" />
                              {formatDate(application.submittedAt)}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-5655">
                              <BuildingIcon className="h-3 w-3 mr-1" data-id="element-5656" />
                              {application.mdaName}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 md:mt-0 flex flex-col items-start md:items-end" data-id="element-5657">
                          <div className="flex items-center" data-id="element-5658">
                            {getStatusBadge(application.status)}
                            {application.isPaid ? <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-5659">
                                <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-5660" />
                                Paid
                              </span> : <span className="ml-2 text-xs text-red-600 flex items-center" data-id="element-5661">
                                <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-5662" />
                                Unpaid
                              </span>}
                          </div>
                          <div className="mt-1 text-xs text-gray-500" data-id="element-5663">
                            Last updated: {formatDate(application.updatedAt)}
                          </div>
                        </div>
                      </div>
                      {/* Workflow Progress */}
                      {applicationWorkflows[application.id] && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-5664">
                          <div className="flex items-center justify-between mb-2" data-id="element-5665">
                            <span className="text-xs font-medium text-gray-700" data-id="element-5666">
                              Approval Progress
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-5667">
                              Step{' '}
                              {applicationWorkflows[application.id].currentStepIndex + 1}{' '}
                              of{' '}
                              {applicationWorkflows[application.id].steps.length}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-5668">
                            <div className="bg-green-600 h-2.5 rounded-full" style={{
                      width: `${(applicationWorkflows[application.id].currentStepIndex + 1) / applicationWorkflows[application.id].steps.length * 100}%`
                    }} data-id="element-5669"></div>
                          </div>
                          <div className="mt-2" data-id="element-5670">
                            <span className="text-xs text-gray-600" data-id="element-5671">
                              Current step:{' '}
                              <span className="font-medium" data-id="element-5672">
                                {applicationWorkflows[application.id].steps[applicationWorkflows[application.id].currentStepIndex].name}
                              </span>
                            </span>
                          </div>
                        </div>}
                      {/* Actions */}
                      <div className="mt-4 flex flex-wrap items-center justify-end space-x-2" data-id="element-5673">
                        {application.status === 'awaiting_payment' && !application.isPaid && <Button variant="primary" size="sm" onClick={() => navigate(`/payment/${application.serviceId}`)} data-id="element-5674">
                              Make Payment
                            </Button>}
                        <Button variant="outline" size="sm" onClick={() => navigate(`/track-application/${application.referenceNumber}`)} className="flex items-center" data-id="element-5675">
                          <ChevronRightIcon className="h-4 w-4 mr-1" data-id="element-5676" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>)}
            </div>}
          {/* Pagination - would be implemented with real data */}
          {filteredApplications.length > 0 && <div className="mt-6 flex items-center justify-between" data-id="element-5677">
              <div className="text-sm text-gray-700" data-id="element-5678">
                Showing{' '}
                <span className="font-medium" data-id="element-5679">
                  {filteredApplications.length}
                </span>{' '}
                of <span className="font-medium" data-id="element-5680">{applications.length}</span>{' '}
                applications
              </div>
              <div className="flex-1 flex justify-end" data-id="element-5681">
                <Button variant="outline" size="sm" onClick={() => navigate('/mdas')} className="flex items-center" data-id="element-5682">
                  <ArrowRightIcon className="h-4 w-4 mr-2" data-id="element-5683" />
                  Apply for New Service
                </Button>
              </div>
            </div>}
        </div>
      </div>
    </UserLayout>;
};
export default MyApplicationsPage;